import { api } from 'api';
import ClearIcon from 'assets/ver2/icons/close-black-icon.svg';
import SearchIcon from 'assets/ver2/icons/search-icon.svg';
import { preventKeyDownEnter } from 'components/common/func';
import {
  DEBOUNCE_SEC,
  DESKTOP_MIN_WIDTH,
  RESET_BACKDROP_STORE,
} from 'constants/common';
import { addressFinderTextValid } from 'helpers';
import { Fade } from 'helpers/animation';
import { checkMaintenance } from 'helpers/maintenance';
import useDebounceCallback from 'hooks/useDebounceCallback';
import useLang from 'hooks/useLang';
import useStore from 'hooks/useStore';
import { useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { SET_SHOW_BACKDROP } from 'store/action';
import styled from 'styled-components';

const FormInputAddressFinderValidation = ({
  field,
  addressFinder,
  setAddressFinder,
  setAddressSuggestion,
  setPickupLocationDataByAusPost,
  setError,
  onTypedTextChange,
}) => {
  const { t } = useLang();
  const { state, dispatch } = useStore();

  const inputRef = useRef(null);

  // Last typed text that failed addressFinderTextValid. The box is only
  // flagged while it still holds exactly that text, so a selected suggestion
  // (written into the DOM without an onChange) is never validated.
  const [invalidText, setInvalidText] = useState(null);
  const invalidTextRef = useRef(null);

  const isTypedTextInvalid = () =>
    invalidTextRef.current !== null &&
    invalidTextRef.current === inputRef.current?.value;
  const isSearchInvalid =
    invalidText !== null && invalidText === inputRef.current?.value;

  const updateInvalidText = (value) => {
    invalidTextRef.current = value;
    setInvalidText(value);
  };

  // A queued or in-flight lookup is dropped when its typed text is invalid or
  // the box has since been cleared (the debounce has no cancel).
  const shouldDropLookup = () =>
    isTypedTextInvalid() || !(inputRef.current?.value ?? '').trim();

  const fetchLookupAddressSuggestion = async (value) => {
    try {
      const { data } = await api.lookupAddressLocality(
        value,
        'd6fe8ff05352dfff3c3a3367d7140364'
      );

      if (shouldDropLookup()) return;

      if (data?.payload) {
        setAddressSuggestion(data.payload);
      }
    } catch (error) {
      checkMaintenance(error);
      console.error(error?.message);
    }
  };
  const checkLookupAddressSuggestion = async (value) => {
    if (shouldDropLookup()) return;

    await fetchLookupAddressSuggestion(value);

    if (shouldDropLookup()) return;

    setPickupLocationDataByAusPost(null);
    setError('');

    dispatch({
      type: SET_SHOW_BACKDROP,
      payload: {
        isShow: true,
        current: field.name,
      },
    });
  };
  const { debounce: handleLookupAddressSuggestion } = useDebounceCallback(
    (value) => {
      checkLookupAddressSuggestion(value);
    },
    DEBOUNCE_SEC
  );

  const onReset = () => {
    updateInvalidText(null);
    onTypedTextChange?.();
    document.getElementsByName(field.name)[0].value = '';
    setAddressSuggestion(null);
    setPickupLocationDataByAusPost(null);
    setError('');
    setAddressFinder(null);
    dispatch({
      type: SET_SHOW_BACKDROP,
      payload: {
        isShow: false,
        current: RESET_BACKDROP_STORE.current,
        onClick: () => {},
      },
    });
  };

  const handleOnChange = (e) => {
    const { value } = e.target;
    const valueTrim = value.trim();

    onTypedTextChange?.();

    if (!valueTrim) {
      setAddressFinder('');
      onReset();
      setPickupLocationDataByAusPost(null);
      setError('');
      return;
    }

    if (!addressFinderTextValid(value)) {
      updateInvalidText(value);
      setAddressFinder(valueTrim);
      setAddressSuggestion(null);
      setPickupLocationDataByAusPost(null);
      setError('');
      if (state?.backdrop?.current === field.name) {
        dispatch({
          type: SET_SHOW_BACKDROP,
          payload: { isShow: false, current: RESET_BACKDROP_STORE.current },
        });
      }
      return;
    }

    updateInvalidText(null);
    setAddressFinder(valueTrim);
    handleLookupAddressSuggestion(valueTrim);
  };

  const handleOnClearSearchInput = () => {
    setAddressFinder('');
    onReset();
  };
  const renderSearchInputIcon = () => {
    if (addressFinder)
      return (
        <SearchInputIconStyled
          src={ClearIcon}
          width={24}
          height={24}
          alt=""
          onClick={handleOnClearSearchInput}
        />
      );
    return (
      <SearchInputIconStyled
        src={SearchIcon}
        width={24}
        height={24}
        alt=""
        onClick={() => {}}
      />
    );
  };

  return (
    <FormGroupStyled onKeyDown={preventKeyDownEnter}>
      <FormControl
        ref={inputRef}
        type={field.type}
        name={field.name}
        defaultValue={addressFinder}
        placeholder={t(field.placeholder)}
        isInvalid={isSearchInvalid}
        onChange={handleOnChange}
      />
      {renderSearchInputIcon()}
      {isSearchInvalid && (
        <SearchErrorText role="alert">
          {t('form_msg_search_validation')}
        </SearchErrorText>
      )}
    </FormGroupStyled>
  );
};

const FormGroupStyled = styled(Form.Group)`
  position: relative;
  margin-bottom: 0px;
  ${Fade};
`;

const FormControl = styled(Form.Control)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;

  color: var(--ds-c-grey-dark);
  border: 1px solid var(--ds-bg-2);
  border-radius: 8px;
  background: var(--ds-c-white);
  width: 100%;
  height: 44px;
  padding: 10px;
  padding-right: 44px;
  margin-inline: 0px;

  &:focus {
    border: 1px solid var(--ds-c-grey-dark) !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus,
  &.is-invalid {
    border: 1px solid var(--ds-c-red) !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
  }

  &[readonly] {
    outline: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
  }

  &::placeholder {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    color: var(--ds-c-blue-disabled) !important;
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
    width: 100%;
  }
`;

const SearchErrorText = styled.p`
  margin: 8px 0 0;
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-red);
  text-transform: none;
`;

const SearchInputIconStyled = styled.img`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
`;

export default FormInputAddressFinderValidation;
